import { Bell, CheckCheck, MapPin, Megaphone, UserCheck, UserPlus, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/db';
import { captureSilentError } from '../services/posthog';
import { useUIStore } from '../stores/useUIStore';
import { AppNotification, NotificationType } from '../types';

// One glyph per notification type so the list is scannable at a glance.
const NOTIFICATION_ICONS: Record<NotificationType, React.ElementType> = {
    friend_request: UserPlus,
    friend_accepted: UserCheck,
    good_shout: Megaphone,
    new_shout: MapPin,
};

interface NotificationsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

// Helper to group notifications by date
const groupByDate = (notifications: AppNotification[]): { today: AppNotification[], yesterday: AppNotification[], earlier: AppNotification[] } => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    return notifications.reduce((acc, notif) => {
        const notifDate = new Date(notif.createdAt);
        const notifDay = new Date(notifDate.getFullYear(), notifDate.getMonth(), notifDate.getDate());

        if (notifDay.getTime() === today.getTime()) {
            acc.today.push(notif);
        } else if (notifDay.getTime() === yesterday.getTime()) {
            acc.yesterday.push(notif);
        } else {
            acc.earlier.push(notif);
        }
        return acc;
    }, { today: [], yesterday: [], earlier: [] } as { today: AppNotification[], yesterday: AppNotification[], earlier: AppNotification[] });
};

// Format relative time
const formatTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const NotificationsModal: React.FC<NotificationsModalProps> = ({ isOpen, onClose }) => {
    const { currentUser } = useAuth();
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { openProfile, selectVenue, setUnreadNotificationCount, closeNotifications } = useUIStore();

    // Fetch notifications on open
    const loadNotifications = useCallback(async () => {
        if (!currentUser) return;
        setIsLoading(true);
        setError(null);
        try {
            const data = await db.getNotifications(currentUser.uid);
            setNotifications(data);
        } catch (e) {
            captureSilentError('NotificationsModal.loadNotifications', e);
            setError('Failed to load notifications');
        } finally {
            setIsLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        if (isOpen && currentUser) {
            loadNotifications();
        }
    }, [isOpen, currentUser, loadNotifications]);

    // Handle notification click
    const handleNotificationClick = async (notif: AppNotification) => {
        // Mark as read
        if (!notif.readAt) {
            try {
                await db.markNotificationRead(notif.id);
                setNotifications(prev => prev.map(n => 
                    n.id === notif.id ? { ...n, readAt: new Date().toISOString() } : n
                ));
                // Update unread count
                const newCount = await db.getUnreadNotificationCount(currentUser!.uid);
                setUnreadNotificationCount(newCount);
            } catch (e) {
                captureSilentError('NotificationsModal.markRead', e);
            }
        }

        // Deep link based on type
        switch (notif.type) {
            case 'friend_request':
            case 'friend_accepted':
                closeNotifications();
                // Small delay to let modal close animation finish
                setTimeout(() => openProfile(), 100);
                break;
            case 'good_shout':
            case 'new_shout':
                if (notif.data?.venueId) {
                    closeNotifications();
                    setTimeout(() => {
                        selectVenue(notif.data!.venueId!);
                        // Pan to venue if we have coordinates (we'd need to fetch venue data)
                        // For now, just select the venue - the Map component should handle panning
                    }, 100);
                }
                break;
        }
    };

    // Mark all as read
    const handleMarkAllRead = async () => {
        if (!currentUser) return;

        // Optimistic Update
        const previousNotifications = [...notifications];
        const previousUnreadCount = useUIStore.getState().unreadNotificationCount;

        // 1. Update local state immediately
        setNotifications(prev => prev.map(n => ({ ...n, readAt: n.readAt || new Date().toISOString() })));
        setUnreadNotificationCount(0);

        try {
            // 2. Perform DB operation in background
            await db.markAllNotificationsRead(currentUser.uid);
        } catch (e) {
            // 3. Revert on failure
            captureSilentError('NotificationsModal.markAllRead', e);
            setNotifications(previousNotifications);
            setUnreadNotificationCount(previousUnreadCount);
            
            // 4. Show error to user
            useUIStore.getState().showToast('Failed to mark notifications as read', 'error');
        }
    };

    if (!isOpen || !currentUser) return null;

    const grouped = groupByDate(notifications);
    const hasUnread = notifications.some(n => !n.readAt);

    const renderNotificationItem = (notif: AppNotification) => {
        const NotifIcon = NOTIFICATION_ICONS[notif.type] ?? Bell;

        return (
        <button
            key={notif.id}
            onClick={() => handleNotificationClick(notif)}
            className={`w-full text-left p-4 rounded-xl transition-all active:scale-[0.98] ${
                notif.readAt 
                    ? 'bg-white hover:bg-brand-cream-dark/50' 
                    : 'bg-brand-cream-dark/40 hover:bg-brand-cream-dark/60'
            }`}
        >
            <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                    notif.readAt ? 'bg-brand-cream-dark' : 'bg-brand-red/10'
                }`}>
                    <NotifIcon size={18} strokeWidth={2.25} className={notif.readAt ? 'text-brand-dark-light' : 'text-brand-red'} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <h4 className={`font-semibold text-sm truncate ${notif.readAt ? 'text-brand-dark-light' : 'text-brand-dark'}`}>
                            {notif.title}
                        </h4>
                        <span className="text-xs text-brand-dark-light shrink-0">
                            {formatTime(notif.createdAt)}
                        </span>
                    </div>
                    <p className={`text-sm mt-0.5 ${notif.readAt ? 'text-brand-dark-light' : 'text-brand-dark'}`}>
                        {notif.body}
                    </p>
                </div>
                {!notif.readAt && (
                    <div className="w-2 h-2 rounded-full bg-brand-red shrink-0 mt-2" />
                )}
            </div>
        </button>
        );
    };

    const renderSection = (title: string, items: AppNotification[]) => {
        if (items.length === 0) return null;
        return (
            <div className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-brand-dark-light px-1">
                    {title}
                </h3>
                <div className="space-y-2">
                    {items.map(renderNotificationItem)}
                </div>
            </div>
        );
    };

    return (
        <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm animate-in fade-in" 
            onClick={onClose}
        >
            <div 
                className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-white">
                    <h3 className="font-display font-extrabold text-xl text-brand-dark">Notifications</h3>
                    <div className="flex items-center gap-2">
                        {hasUnread && (
                            <button 
                                onClick={handleMarkAllRead}
                                className="p-2 hover:bg-brand-cream-dark rounded-full text-brand-dark-light hover:text-brand-dark transition-colors"
                                title="Mark all as read"
                            >
                                <CheckCheck size={20} />
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 hover:bg-brand-cream-dark rounded-full" aria-label="Close">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="overflow-y-auto flex-1 bg-brand-cream p-4">
                    {isLoading ? (
                        <div className="space-y-4 animate-pulse">
                            {[...Array(3)].map((_, i) => (
                                <div key={i} className="bg-white rounded-xl p-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-full bg-brand-cream-dark" />
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-brand-cream-dark rounded w-3/4" />
                                            <div className="h-3 bg-brand-cream-dark rounded w-1/2" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center p-8 bg-white border border-brand-red/20 rounded-2xl text-center space-y-4">
                            <p className="text-brand-red font-bold">{error}</p>
                            <button 
                                onClick={loadNotifications}
                                className="px-6 py-2 bg-brand-red text-white font-bold rounded-lg shadow hover:bg-brand-red-hover transition active:scale-95"
                            >
                                Retry
                            </button>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="w-16 h-16 rounded-full bg-brand-cream-dark flex items-center justify-center mb-4">
                                <Bell size={32} className="text-brand-dark-light" />
                            </div>
                            <h4 className="font-display font-extrabold text-lg text-brand-dark mb-1">No notifications yet</h4>
                            <p className="text-sm text-brand-dark-light">
                                When friends interact with you, you'll see it here
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {renderSection('Today', grouped.today)}
                            {renderSection('Yesterday', grouped.yesterday)}
                            {renderSection('Earlier', grouped.earlier)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
